/**
 * Part of main Application Store.
 */
import { ref } from 'vue';
import { defineStore } from 'pinia';
import { associateBy } from '@std/collections';

import type { DogTagComponent, DogtagResponse, DogtagStore as DogtagStoreState } from './types';
import type { Clan } from '@/types/vortex';

import {
  fetchClanInfoByAccountId,
  fetchDogtagComponentsByAccountIds,
  type GlossaryResponse,
} from '@/composables/network-manager';

import { ID, TTL } from './config';
import { clear, loadFromStorage, saveToStorage } from './storage';

const isInit = ref(false);

export const useDogtagStore = defineStore(ID, () => {
  const store = ref<DogtagStoreState>({
    account: {},
    clan: {},
  });

  const fillData = (data: GlossaryResponse | null) => {
    isInit.value = true;
    store.value.component = associateBy(
      data?.data.dogTagComponents as DogTagComponent[],
      (i) => i.id ?? ''
    );
    const storageData = loadFromStorage();

    const now = new Date().getTime();
    const isExpired = storageData && now - Number(storageData.ttl ?? 0) > TTL;
    if (isExpired) {
      console.log('isExpired');
      clear();
    }

    store.value.clan = storageData?.clan ?? {};
    store.value.account = storageData?.account ?? {};
  };

  const getClanDataByAccountId = async (accountId: number | string): Promise<Clan | undefined> => {
    let result: Clan | undefined = undefined;

    if (store.value.clan && accountId in store.value.clan) {
      result = store.value.clan[accountId];
    }

    if (!result) {
      result = await fetchClanInfoByAccountId(accountId);
    }

    // Add result info to store
    if (result) {
      store.value.clan[accountId] = result;
    }

    saveToStorage(store.value);

    return result;
  };

  const storageData = loadFromStorage();
  const accountIdList = ref(Object.keys(storageData?.account ?? {}));

  const { data: resData, status } = fetchDogtagComponentsByAccountIds(accountIdList);
  const unwatch = watch(status, (val) => {
    if (val === 'success' && resData.value) {
      for (const accountId in resData.value.data) {
        if (Object.hasOwn(resData.value.data, accountId)) {
          const componentsIdObject = resData.value.data[accountId]?.dog_tag.selected_dog_tag;
          if (componentsIdObject) {
            const componentIdList = Object.values(componentsIdObject);
            store.value.account[accountId] = componentIdList.filter((item) => item !== 0);
          }

          saveToStorage(store.value);
        }
      }
    }
  });

  /**
   * Adds account ID(s) to the list for tracking dogtag data.
   * Filters out IDs that are already present in the accountIdList.
   * @param values
   */
  const add = (values: string | string[]) => {
    const valueArray = Array.isArray(values) ? values : [values];
    const newIds = valueArray.filter((id) => !accountIdList.value.includes(id));
    console.log('newIds', newIds); // @TODO: Remove after testing

    if (newIds.length) {
      accountIdList.value.push(...newIds);
    }
  };

  /**
   * Returns a computed reference to the Dogtag component map for the specified account.
   * Retrieves dogtag data from the network response and maps components by type.
   * @param accountId
   */
  const useDogtag = (accountId: string) =>
    computed(() => {
      const componentsIdObject = store.value.account[accountId];
      if (componentsIdObject) {
        const componentIdList = Object.values(componentsIdObject);

        const componentList = componentIdList
          .map((id) => store.value.component?.[id])
          .filter((item): item is NonNullable<typeof item> => item !== undefined);

        return associateBy<DogTagComponent>(componentList, (component) => component.type);
      }

      return undefined;
    });

  return {
    state: store,
    fillData,
    isInit,
    getClanDataByAccountId,
    add,
    useDogtag,
  };
});

export type DogtagStore = ReturnType<typeof useDogtagStore>;

export type { DogtagResponse };
